import { motion } from "framer-motion";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const initialsOf = (name) =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export default function TeamSection() {
  const content = useContent();
  const { t } = useLang();
  const team = content.team || [];
  if (team.length === 0) return null;

  return (
    <section id="team" data-testid="team-section" className="border-t border-line bg-moss/40">
      <div className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-36">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="text-xs uppercase tracking-[0.3em] text-harvest"
        >
          {t("teamOverline")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease }}
          data-testid="team-heading"
          className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl"
        >
          {t("teamHeadA")} <span className="italic text-harvest">{t("teamHeadAccent")}</span>
        </motion.h2>

        <div className="mt-16 flex flex-wrap justify-center gap-6 lg:justify-start">
          {team.map((e, i) => (
            <motion.div
              key={`${e.name}-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease }}
              data-testid={`team-card-${i}`}
              className="w-full border border-line bg-moss p-8 transition-transform duration-500 hover:-translate-y-1 sm:w-[380px]"
            >
              {e.photo ? (
                <img
                  src={e.photo}
                  alt={e.name}
                  className="h-16 w-16 border border-line object-cover"
                />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center border border-harvest/40 font-display text-xl italic text-harvest">
                  {initialsOf(e.name)}
                </span>
              )}
              <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-cream">
                {e.name}
              </h3>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-harvest">
                {e.designation}
              </p>
              {e.sales && e.sales !== "—" ? (
                <div className="mt-6 border-t border-line pt-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-sage">{t("totalSales")}</p>
                  <p className="mt-1 font-display text-3xl font-medium tracking-tight text-cream">
                    {e.sales}
                  </p>
                </div>
              ) : null}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
